import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  detectUserTimezone, 
  detectUserLocation,
  getCommonTimezones,
  updateAgentSchedule,
  updateAgentLocation
} from '../../lib/services/timezone-location-service.js';
import Button from './ui/Button';
import Icon from './AppIcon';

// DB stores 1=Mon … 7=Sun; checkbox row index is 0=Sun … 6=Sat (JS getDay)
function storageDayFromCheckboxIndex(index) {
  return index === 0 ? 7 : index;
}

function checkboxIndexFromStorageDay(storageDay) {
  return storageDay === 7 ? 0 : storageDay;
}

function normalizeStoredScheduleDays(days) {
  return (days || [1, 2, 3, 4, 5]).map((d) => (d === 0 ? 7 : d));
}

const LocationTimezoneSettings = () => {
  const { userProfile, updateProfile } = useAuth();
  const [timezone, setTimezone] = useState('America/New_York');
  const [location, setLocation] = useState({ city: '', state: '', country: 'US' });
  const [schedule, setSchedule] = useState({
    startTime: '09:00',
    endTime: '17:00',
    allowedDays: [1, 2, 3, 4, 5], // Monday-Friday
    enabled: true
  });
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setTimezone(userProfile.timezone || detectUserTimezone());
      setLocation({
        city: userProfile.location_city || '',
        state: userProfile.location_state || '',
        country: userProfile.location_country || 'US'
      });
      // Normalize times from database (may be HH:MM:SS format) to HH:MM for time input
      const normalizeTime = (timeStr) => {
        if (!timeStr) return timeStr;
        return timeStr.length > 5 ? timeStr.substring(0, 5) : timeStr;
      };
      
      setSchedule({
        startTime: normalizeTime(userProfile.sms_schedule_start) || '09:00',
        endTime: normalizeTime(userProfile.sms_schedule_end) || '17:00',
        allowedDays: normalizeStoredScheduleDays(userProfile.sms_schedule_days),
        enabled: userProfile.sms_schedule_enabled !== false
      });
    }
  }, [userProfile]);

  const handleDetectLocation = async () => {
    setLocationLoading(true);
    try {
      const detectedLocation = await detectUserLocation();
      setLocation({
        city: detectedLocation.city || '',
        state: detectedLocation.state || '',
        country: detectedLocation.country || 'US'
      });
      setTimezone(detectedLocation.timezone || detectUserTimezone());
    } catch (error) {
      console.error('Error detecting location:', error);
      alert('Location detection failed. Please enter manually.');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      if (userProfile?.id) {
        // Normalize times to HH:MM format (remove seconds if present)
        const normalizeTime = (timeStr) => {
          if (!timeStr) return timeStr;
          return timeStr.length > 5 ? timeStr.substring(0, 5) : timeStr;
        };
        
        // Update both location and schedule in one call
        const { data, error } = await supabase
          .from('user_profiles')
          .update({
            timezone: timezone,
            location_city: location.city,
            location_state: location.state,
            location_country: location.country,
            sms_schedule_start: normalizeTime(schedule.startTime),
            sms_schedule_end: normalizeTime(schedule.endTime),
            sms_schedule_days: schedule.allowedDays,
            sms_schedule_enabled: schedule.enabled
          })
          .eq('id', userProfile.id)
          .select();
        
        if (error) {
          throw error;
        }
        
        // Refresh the user profile to show updated data
        if (updateProfile) {
          await updateProfile();
        }
        
        alert('Settings saved successfully!');
      } else {
        alert('Error: No user profile found. Please log in again.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(`Failed to save settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="space-y-6">
      {/* Location Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Location Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">City</label>
            <input
              type="text"
              value={location.city}
              onChange={(e) => setLocation({...location, city: e.target.value})}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              placeholder="New York"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">State</label>
            <input
              type="text"
              value={location.state}
              onChange={(e) => setLocation({...location, state: e.target.value})}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              placeholder="NY"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">Timezone</label>
          <div className="flex items-center space-x-3">
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              {getCommonTimezones().map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <Button
              onClick={handleDetectLocation}
              disabled={locationLoading}
              className="px-4 py-2"
            >
              <Icon name="MapPin" size={16} className="mr-2" />
              {locationLoading ? 'Detecting...' : 'Detect Location'}
            </Button>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          <p>Current time: {new Date().toLocaleString('en-US', { timeZone: timezone })}</p>
          <p>Location: {location.city && location.state ? `${location.city}, ${location.state}` : 'Not set'}</p>
        </div>
      </div>

      {/* SMS Schedule Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">SMS Schedule Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Start Time</label>
            <input
              type="time"
              value={schedule.startTime}
              onChange={(e) => setSchedule({...schedule, startTime: e.target.value})}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">End Time</label>
            <input
              type="time"
              value={schedule.endTime}
              onChange={(e) => setSchedule({...schedule, endTime: e.target.value})}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">Allowed Days</label>
          <div className="flex flex-wrap gap-2">
            {dayNames.map((day, index) => {
              const storageDay = storageDayFromCheckboxIndex(index);
              return (
              <label key={index} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={schedule.allowedDays.includes(storageDay)}
                  onChange={(e) => {
                    const newDays = e.target.checked
                      ? [...schedule.allowedDays, storageDay]
                      : schedule.allowedDays.filter(d => d !== storageDay);
                    setSchedule({...schedule, allowedDays: newDays.sort((a, b) => a - b)});
                  }}
                  className="rounded border-border"
                />
                <span className="text-sm text-foreground">{day}</span>
              </label>
            );
            })}
          </div>
        </div>

        <div className="mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={schedule.enabled}
              onChange={(e) => setSchedule({...schedule, enabled: e.target.checked})}
              className="rounded border-border"
            />
            <span className="text-sm font-medium text-foreground">Enable SMS scheduling</span>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={loading}
          className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Icon name="Save" size={16} className="mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Status */}
      <div className="bg-green-50 border border-green-200 rounded-md p-3">
        <div className="flex items-center">
          <Icon name="CheckCircle" size={16} className="text-green-600 mr-2" />
          <span className="text-sm text-green-800">
            Settings component is working! All features restored.
          </span>
        </div>
      </div>
    </div>
  );
};

export default LocationTimezoneSettings;